import { useEffect, useState } from "react";

import { useLatestRequestGuard } from "@/hooks/useLatestRequestGuard";
import {
  AnalyticsServiceV2,
  type AreaSessionMetrics,
} from "@/services/features/AnalyticsServiceV2";
import { type Store, StoreService } from "@/services/features/StoreService";
import { type DateRange, storePerformanceService } from "@/services/StorePerformanceService";
import { getBrowserTimezone } from "@/utils/storeTime";

export interface FittingRoomAreaUsage {
  id: number | null;
  name: string | null;
  totalSessions: number;
  rooms: FittingRoomUsage[];
}

export interface FittingRoomUsage {
  id: number;
  name: string | null;
  totalSessions: number;
}

interface UseFittingRoomAreaUsageOptions {
  store: Store | null;
  currentRange: DateRange;
}

const emptyAreas: FittingRoomAreaUsage[] = [];

export const mapAreaSessionUsage = (areas: AreaSessionMetrics[]): FittingRoomAreaUsage[] =>
  areas
    .map((area) => ({
      id: area.areaId,
      name: area.areaName,
      totalSessions: area.totalSessions ?? 0,
      rooms: (area.rooms ?? [])
        .map((room) => ({
          id: room.changingRoomId,
          name: room.changingRoomName,
          totalSessions: room.totalSessions ?? 0,
        }))
        .sort((left, right) => right.totalSessions - left.totalSessions),
    }))
    .sort((left, right) => right.totalSessions - left.totalSessions);

export const useFittingRoomAreaUsage = ({
  store,
  currentRange,
}: UseFittingRoomAreaUsageOptions) => {
  const beginRequest = useLatestRequestGuard();
  const browserTimezone = getBrowserTimezone();
  const startDate = storePerformanceService.formatDate(currentRange.start, browserTimezone);
  const endDate = storePerformanceService.formatDate(currentRange.end, browserTimezone);
  const [data, setData] = useState<FittingRoomAreaUsage[]>(emptyAreas);
  const [areaCount, setAreaCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const isCurrent = beginRequest();
    if (!store || !startDate || !endDate) {
      setData(emptyAreas);
      setAreaCount(null);
      setLoading(false);
      setError(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(false);
      setAreaCount(null);
      try {
        const areaResponse = await StoreService.getInstance().getChangingRoomsByStore(store.id);
        if (!areaResponse.success || !areaResponse.data) {
          throw new Error(areaResponse.error ?? "Failed to load fitting room areas");
        }
        if (!isCurrent()) return;
        setAreaCount(areaResponse.data.length);
        if (areaResponse.data.length === 0) {
          setData(emptyAreas);
          return;
        }

        const sessionsResponse = await AnalyticsServiceV2.getInstance().getAreaSessionAnalytics({
          storeId: store.id,
          startDate,
          endDate,
          roomType: "FITTING_ROOM",
        });
        if (isCurrent()) {
          setData(mapAreaSessionUsage(sessionsResponse.data ?? []));
        }
      } catch (loadError) {
        console.error("Failed to load fitting room area usage:", loadError);
        if (isCurrent()) {
          setData(emptyAreas);
          setError(true);
        }
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };

    void load();
  }, [beginRequest, endDate, startDate, store]);

  return { areaCount, data, error, loading };
};
